import { Node, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ySyncPluginKey } from '@tiptap/y-tiptap';
/** De plaatshouder zolang er voor een citatie geen uitkomst is, naar CitatenPOC (`'(…)'`). */
const REFERENCE_PLACEHOLDER = '(…)';
function createCitationLabels() {
    let current = new Map();
    const listeners = new Set();
    return {
        get: (citationId) => current.get(citationId),
        set(labels) {
            if (labels === current)
                return;
            current = labels;
            for (const listener of listeners)
                listener();
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
    };
}
function createCitationPopover() {
    let state = null;
    const listeners = new Set();
    const notify = () => {
        for (const listener of listeners)
            listener();
    };
    return {
        current: () => state,
        open(next) {
            state = next;
            notify();
        },
        close(anchor, via) {
            if (state === null)
                return;
            if (anchor !== undefined && state.anchor !== anchor)
                return;
            if (via !== undefined && state.via !== via)
                return;
            state = null;
            notify();
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
    };
}
export const Reference = Node.create({
    name: 'referentie',
    group: 'inline',
    inline: true,
    atom: true,
    addStorage() {
        return {
            labels: createCitationLabels(),
            popover: createCitationPopover(),
            popoverId: '',
            newCitationId: () => `cit-${crypto.randomUUID()}`,
        };
    },
    /**
     * Elke citatie houdt een eigen id, naar CitatenPOC's `uniqueCitationIdPlugin`.
     *
     * Kopiëren en plakken kloont de attributen, en twee knopen met hetzelfde id delen dan één label en
     * één vermelding: de engine houdt de eerste. Hier krijgt de latere een nieuw id, ook als de eerste
     * in een ander veld of een herhaalgroeprij van de laag staat (dan kennen de labels haar). Alleen bij een
     * eigen wijziging en niet bij een die van de samenwerklaag komt, zodat niet elke medeschrijver
     * tegelijk herschrijft: wie de dubbel maakte, heelt hem, en dat komt via Yjs bij de rest. Buiten
     * de historie, en idempotent: zijn de ids uniek, dan doet de volgende ronde niets.
     */
    addProseMirrorPlugins() {
        const storage = this.storage;
        return [
            new Plugin({
                key: new PluginKey('uniekeCitatieId'),
                appendTransaction(transactions, before, state) {
                    const local = transactions.some((tr) => tr.docChanged && tr.getMeta(ySyncPluginKey)?.isChangeOrigin !== true);
                    if (!local)
                        return null;
                    // Ook een id dat al in een ander veld of een rij van de laag staat (de labels van de laag
                    // kennen het) en dat hier nieuw binnenkomt: een citatie die van daar is geplakt.
                    const had = new Set();
                    before.doc.descendants((node) => {
                        if (node.type.name === 'referentie')
                            had.add(String(node.attrs['citatieId']));
                    });
                    const seen = new Set();
                    const duplicates = [];
                    state.doc.descendants((node, pos) => {
                        if (node.type.name !== 'referentie')
                            return;
                        const id = node.attrs['citatieId'];
                        if (id === null || id === '')
                            return;
                        const elsewhere = !had.has(id) && storage.labels.get(id) !== undefined;
                        if (seen.has(id) || elsewhere)
                            duplicates.push(pos);
                        else
                            seen.add(id);
                    });
                    if (duplicates.length === 0)
                        return null;
                    const tr = state.tr;
                    for (const pos of duplicates)
                        tr.setNodeAttribute(pos, 'citatieId', storage.newCitationId());
                    return tr.setMeta('addToHistory', false);
                },
            }),
        ];
    },
    addNodeView() {
        return ({ node: initial, editor }) => {
            let node = initial;
            const dom = document.createElement('span');
            dom.className = 'ef-referentie';
            dom.contentEditable = 'false';
            const { labels, popover } = editor.storage.referentie;
            const citationId = () => String(node.attrs['citatieId'] ?? '');
            const paint = () => {
                dom.setAttribute('data-referentie', citationId());
                dom.textContent = labels.get(citationId()) ?? REFERENCE_PLACEHOLDER;
                // In een alleen-lezen veld is er geen cursor om de citatie mee te kiezen: dan is ze zelf
                // bereikbaar met Tab (R-139, R-158).
                if (editor.isEditable)
                    dom.removeAttribute('tabindex');
                else
                    dom.tabIndex = 0;
            };
            const describe = () => {
                const state = popover.current();
                if (state?.anchor === dom && editor.storage.referentie.popoverId !== '') {
                    dom.setAttribute('aria-describedby', editor.storage.referentie.popoverId);
                }
                else
                    dom.removeAttribute('aria-describedby');
            };
            const open = (via) => () => popover.open({ citationId: citationId(), anchor: dom, via });
            const onEnter = open('muis');
            const onFocus = open('focus');
            const onLeave = () => popover.close(dom, 'muis');
            const onBlur = () => popover.close(dom, 'focus');
            dom.addEventListener('mouseenter', onEnter);
            dom.addEventListener('mouseleave', onLeave);
            dom.addEventListener('focus', onFocus);
            dom.addEventListener('blur', onBlur);
            paint();
            const stopLabels = labels.subscribe(paint);
            const stopPopover = popover.subscribe(describe);
            const stopEditable = () => paint();
            editor.on('update', stopEditable);
            return {
                dom,
                selectNode() {
                    dom.classList.add('ProseMirror-selectednode');
                    popover.open({ citationId: citationId(), anchor: dom, via: 'selectie' });
                },
                deselectNode() {
                    dom.classList.remove('ProseMirror-selectednode');
                    popover.close(dom, 'selectie');
                },
                update(next) {
                    if (next.type !== node.type)
                        return false;
                    node = next;
                    paint();
                    return true;
                },
                // Het label is van ons en niet van de invuller: een wijziging erin is geen bewerking. Een
                // selectie wel, zoals bij de standaardweergave van ProseMirror: anders raakt de editor
                // de cursor kwijt na een klik op het label.
                ignoreMutation: (mutation) => mutation.type !== 'selection',
                destroy() {
                    stopLabels();
                    stopPopover();
                    editor.off('update', stopEditable);
                    dom.removeEventListener('mouseenter', onEnter);
                    dom.removeEventListener('mouseleave', onLeave);
                    dom.removeEventListener('focus', onFocus);
                    dom.removeEventListener('blur', onBlur);
                    popover.close(dom);
                },
            };
        };
    },
    addAttributes() {
        return {
            // Naar en uit HTML voor het klembord: zonder deze twee kwam een gekopieerde citatie terug met
            // `bronnen` als tekst in plaats van een lijst, en dan telde ze nergens meer mee.
            citatieId: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-referentie'),
                renderHTML: (attributes) => ({ 'data-referentie': attributes['citatieId'] }),
            },
            bronnen: {
                default: [],
                parseHTML: (element) => sourcesFromHtml(element.getAttribute('data-bronnen')),
                renderHTML: (attributes) => ({ 'data-bronnen': JSON.stringify(attributes['bronnen'] ?? []) }),
            },
        };
    },
    parseHTML() {
        return [{ tag: 'span[data-referentie]' }];
    },
    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(HTMLAttributes, { class: 'ef-referentie' }),
            REFERENCE_PLACEHOLDER,
        ];
    },
});
/** De bron-id's uit `data-bronnen`, of geen bij iets wat geen lijst van teksten is. */
function sourcesFromHtml(raw) {
    if (raw === null)
        return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
    }
    catch {
        return [];
    }
}
//# sourceMappingURL=reference.js.map